
import { Permission } from "../../types";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { preparePermissionsForSave } from "../utils/dataTransformers";

/**
 * Save permissions to database with robust error handling
 */
export const savePermissionsToDatabase = async (permissions: Permission[]): Promise<boolean> => {
  try {
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] [PermissionsStorage] Beginning database save operation`);
    
    if (!Array.isArray(permissions) || permissions.length === 0) {
      console.error("[PermissionsStorage] Invalid permissions data for database save");
      toast.error("Geen geldige rechtendata om op te slaan");
      return false;
    }
    
    // First check database connection
    console.log(`[${timestamp}] [PermissionsStorage] Testing database connection`);
    
    try {
      const { error: testError } = await supabase
        .from('role_permissions')
        .select('id')
        .limit(1);
        
      if (testError) {
        console.error(`[${timestamp}] [PermissionsStorage] Database connection failed:`, testError);
        toast.error("Geen verbinding met database, wijzigingen worden niet opgeslagen");
        return false;
      }
      
      console.log(`[${timestamp}] [PermissionsStorage] Database connection successful`);
    } catch (connectionError) {
      console.error(`[${timestamp}] [PermissionsStorage] Database connection error:`, connectionError);
      toast.error("Geen verbinding met database, wijzigingen worden niet opgeslagen");
      return false;
    }
    
    // Always use upsert for safety (no moment where all permissions are deleted)
    console.log(`[${timestamp}] [PermissionsStorage] Starting upsert save`);
    return await saveWithUpsert(permissions, timestamp);
  } catch (error: any) {
    console.error("[PermissionsStorage] Unhandled error during database save:", error);
    toast.error(`Onverwachte fout bij opslaan: ${error?.message || 'Onbekende fout'}`);
    return false;
  }
};

/**
 * Save permissions using the upsert method (when admin access is not available)
 */
async function saveWithUpsert(permissions: Permission[], timestamp: string): Promise<boolean> {
  console.error(`[${timestamp}] [PermissionsStorage] Admin client not available, using upsert method instead of delete+insert`);
  
  try {
    // Prepare permissions for upsert by handling numeric IDs
    const cleanedPermissions = preparePermissionsForSave(permissions);
    
    // Perform upsert instead of delete+insert
    const { data, error: upsertError } = await supabase
      .from('role_permissions')
      .upsert(cleanedPermissions, { 
        onConflict: 'role,resource,company',
        ignoreDuplicates: false
      })
      .select();
    
    if (upsertError) {
      console.error(`[${timestamp}] [PermissionsStorage] Error upserting permissions:`, upsertError);
      toast.error(`Fout bij opslaan van rechten: ${upsertError.message || 'Controleer database instellingen'}`);
      
      // Provide more specific guidance based on error type
      if (upsertError.code === '42501') { // Permission denied error
        toast.error("Toegang geweigerd. Controleer of RLS-policies correct zijn ingesteld voor deze tabel");
        console.error(`[${timestamp}] [PermissionsStorage] RLS policy error. Make sure to disable RLS or add appropriate policies for role_permissions table`);
      }
      
      return false;
    }
    
    console.log(`[${timestamp}] [PermissionsStorage] Successfully upserted ${data?.length || 0} permissions`);
    toast.success(`Rechten succesvol opgeslagen (${data?.length || 0} items)`);
    return true;
  } catch (upsertError: any) {
    console.error(`[${timestamp}] [PermissionsStorage] Upsert operation failed:`, upsertError);
    toast.error(`Fout bij opslaan van rechten: ${upsertError?.message || 'Onbekende fout'}`);
    return false;
  }
}

